import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";

import { overviewWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/overviewWidgetSizes";
import { WidgetEmployeeSchedulePreview } from "./WidgetEmployeeSchedulePreview";
import { WidgetEmployeeScheduleSettings } from "./WidgetEmployeeScheduleSettings";
import { WAEmployeeScheduleMutation } from "./__generated__/WAEmployeeScheduleMutation.graphql";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WidgetScreen } from "../../common/WidgetScreen";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WAEmployeeScheduleQuery } from "./__generated__/WAEmployeeScheduleQuery.graphql";
import { useWidgetEmployeeSchedulePreviewQueryLoader } from "./useWidgetEmployeeSchedulePreviewQueryLoader";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";

type Props = UserLoggedInNavigatorScreenProps<"WidgetAddEmployeeSchedule">;

export const WAEmployeeSchedule = withWebModal(WAEmployeeScheduleC, true);
function WAEmployeeScheduleC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWAEmployeeSchedule {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWAEmployeeSchedule(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WAEmployeeScheduleQuery>(
    graphql`
      query WAEmployeeScheduleQuery {
        store {
          ...WidgetEmployeeScheduleSettingsQuery_store
        }
      }
    `,
    {},
    { fetchKey: props.fetchKey }
  );

  const [teamIds, setTeamIds] = useState(props.route.params.defaultTeams);

  const query = useWidgetEmployeeSchedulePreviewQueryLoader({
    storecastId: props.route.params.storecastId,
    teamIds,
  });

  const [createWidgetEmployeeSchedule, loading] =
    useMutation<WAEmployeeScheduleMutation>(graphql`
      mutation WAEmployeeScheduleMutation(
        $storecastId: ID!
        $input: CreateWidgetEmployeeScheduleInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetEmployeeSchedule(widget: $input) {
            id
          }
        }
      }
    `);

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      const position = props.route.params.position;
      createWidgetEmployeeSchedule({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX: position.x + position.c * 2,
            positionY: position.y,
            size: WAWidgetSizeToOverviewSize(size),
            teamIds,
          },
        },
        onError: (e) => {
          console.log("e", e);
        },
        onCompleted: () => {
          props.navigation.pop(2);
        },
      });
    },
    [
      createWidgetEmployeeSchedule,
      props.navigation,
      props.route.params.position,
      props.route.params.storecastId,
      teamIds,
    ]
  );

  const renderPreview = useCallback(
    () =>
      query ? (
        <WidgetEmployeeSchedulePreview
          storecastId={props.route.params.storecastId}
          query={query}
        />
      ) : null,
    [props.route.params.storecastId, query]
  );
  return (
    <WidgetScreen
      preview={
        <WASizePreview
          onCreate={onCreate}
          loading={loading}
          unitSize={props.route.params.unitSize}
          maxSize={props.route.params.maxSize}
          widgetSizes={overviewWidgetSizes}
        >
          {renderPreview}
        </WASizePreview>
      }
      settings={
        <WidgetEmployeeScheduleSettings
          store={data.store}
          teamIds={teamIds}
          setTeamIds={setTeamIds}
        />
      }
    />
  );
}
